import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Search, ScanLine, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./ui/kit";
import MobilMenu from "./MobilMenu";
import Hledani from "./Hledani";
import Upozorneni from "./Upozorneni";
import { PocetNaZvonku, useUpozorneni } from "./Zvonek";
import { useOdznaky } from "../lib/mobilniNav";
import { canSee } from "../lib/nav";
import { nahratDoklad } from "../lib/nahratDoklad";
import { apiError } from "../lib/api";

/**
 * Horní lišta na telefonu a tabletu (plátno 16A): žádný pruh, jen skleněné
 * ostrůvky nad obsahem. Vlevo fotka (profil) a zvonek s číslem nepřečtených, uprostřed hledání,
 * vpravo skener dokladu a menu modulů. Pod stavovým řádkem obsah mizí do rozmazání.
 */
const KULATE = "tap h-11 w-11 rounded-full sklo-tm inline-flex items-center justify-center shrink-0 active:scale-95 transition-transform motion-reduce:transition-none";

export default function MobilHlava() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const odznaky = useOdznaky();
  const upozorneni = useUpozorneni();
  const [okno, setOkno] = useState(null);           // "menu" | "hledat" | "zvonek" | null
  const [sken, setSken] = useState("");             // hláška ze čtení dokladu
  const [skenChyba, setSkenChyba] = useState("");
  const soubor = useRef(null);
  // Skener čte doklady — jen kdo smí do Dokladů (stejné role jako v lib/nav.js).
  const smiDoklady = canSee(user, ["OWNER", "OBCHODNIK"]) && !(user?.roles || []).includes("SUBDODAVATEL");

  const zavrit = useCallback(() => setOkno(null), []);
  // Po přechodu na jinou stránku se otevřené okno zavře (třeba zpět v prohlížeči).
  useEffect(() => { setOkno(null); }, [pathname]);
  const otevrit = (co) => {
    if (co !== "hledat") odznaky.obnovit();
    if (co === "zvonek") upozorneni.obnovit();
    setOkno(co);
  };

  const naskenovano = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setSkenChyba("");
    try {
      const fid = await nahratDoklad(files, setSken);
      setSken("");
      nav(`/doklady/${fid}`);
    } catch (err) {
      setSken("");
      setSkenChyba(err.response ? apiError(err.response?.data?.detail) : err.message);
      setTimeout(() => setSkenChyba(""), 5000);
    }
  };

  return (
    <>
      <div aria-hidden="true" className="lg:hidden fixed top-0 inset-x-0 md:left-[68px] z-20 h-[calc(env(safe-area-inset-top)+76px)] pointer-events-none zavoj-nahore" />
      <header data-testid="mobil-hlava"
        className="lg:hidden fixed top-0 inset-x-0 md:left-[68px] z-30 px-3.5 pt-[calc(env(safe-area-inset-top)+8px)] pb-2 flex items-center gap-2 pointer-events-none [&>*]:pointer-events-auto">
        <button type="button" onClick={() => nav("/nastaveni")} aria-label="Profil a nastavení" data-testid="mobil-profil"
          className="tap h-11 w-11 rounded-full shrink-0 shadow-[0_0_0_2px_rgba(255,255,255,.9),0_4px_12px_rgba(20,22,26,.18)] overflow-hidden active:scale-95 transition-transform motion-reduce:transition-none">
          <Avatar name={user?.name} url={user?.photo_url} size={44} className="!rounded-full" />
        </button>
        <button type="button" onClick={() => otevrit("zvonek")}
          aria-label={upozorneni.neprectenych ? `Upozornění: ${upozorneni.neprectenych} nepřečtených` : "Upozornění"}
          data-testid="mobil-zvonek" className={`${KULATE} relative`}>
          <Bell size={19} strokeWidth={2} />
          <PocetNaZvonku n={upozorneni.neprectenych} ring="ring-[#2a2d32]" />
        </button>
        <button type="button" onClick={() => otevrit("hledat")} data-testid="mobil-hledat"
          className="flex-1 min-w-0 h-11 rounded-full sklo-tm flex items-center gap-2 px-4 text-[15px] active:scale-[.98] transition-transform motion-reduce:transition-none">
          <Search size={18} strokeWidth={2.2} className="shrink-0" />
          <span className="truncate text-white/80">Hledat</span>
        </button>
        {smiDoklady && (
          <>
            <input ref={soubor} type="file" accept="image/*,.pdf" capture="environment" multiple className="hidden"
              onChange={naskenovano} data-testid="mobil-sken-soubor" />
            <button type="button" onClick={() => soubor.current?.click()} disabled={!!sken} aria-label="Naskenovat doklad"
              data-testid="mobil-sken" className={KULATE}>
              <ScanLine size={19} strokeWidth={2} />
            </button>
          </>
        )}
        <button type="button" onClick={() => otevrit("menu")} aria-label="Menu" data-testid="menu-button" className={KULATE}>
          <Menu size={19} strokeWidth={2} />
        </button>
      </header>

      {(sken || skenChyba) && (
        <div role="status" data-testid="mobil-sken-stav"
          className={`lg:hidden fixed z-40 left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+64px)] max-w-[calc(100vw-32px)] rounded-full px-4 py-2.5 t-meta font-600 shadow-panel ${
            skenChyba ? "bg-danger text-white" : "bg-ink text-white"}`}>
          {skenChyba || sken}
        </div>
      )}

      {okno === "menu" && <MobilMenu onClose={zavrit} odznaky={odznaky} />}
      {okno === "hledat" && <Hledani onClose={zavrit} />}
      {okno === "zvonek" && <Upozorneni odznaky={odznaky} onClose={zavrit} />}
    </>
  );
}
